import { useState, useEffect, useCallback } from 'react';
import { getConfig, updateConfig as updateConfigApi, deploy as deployApi } from '../utils/api';
import { normalizeConfig } from '../utils/dataNormalizers';

// Sanitize configuration data for API transmission
function sanitizeConfigForAPI(config) {
  if (!config || typeof config !== 'object') {
    return config;
  }
  
  // Deep clone to avoid mutating original
  const sanitized = JSON.parse(JSON.stringify(config));
  
  // Remove any React-specific or problematic properties
  const problematicKeys = ['__reactInternalInstance', '_owner', '$$typeof', 'key', 'ref'];
  
  function cleanObject(obj, path = '') {
    if (!obj || typeof obj !== 'object') return obj;
    
    if (Array.isArray(obj)) {
      return obj.map((item, index) => cleanObject(item, `${path}[${index}]`));
    }
    
    for (const key of problematicKeys) {
      if (key in obj) {
        console.warn(`[Config Sanitizer] Removing problematic key '${key}' at ${path}.${key}`);
        delete obj[key];
      }
    }
    
    // Recursively clean nested objects
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        obj[key] = cleanObject(value, path ? `${path}.${key}` : key);
      }
    }
    
    return obj;
  }
  
  return cleanObject(sanitized);
}

export const useConfig = (initialSiteId = 'lovedeco') => {
  const [siteId, setSiteId] = useState(initialSiteId);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchConfig = useCallback(async (targetSiteId = siteId) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 [useConfig] Fetching unified config for site:', targetSiteId);
      
      // Fetch unified configuration
      const configData = await getConfig(targetSiteId);
      
      console.log('✅ [useConfig] Fetched unified config:', configData);
      
      // Validate unified config structure
      if (!configData || typeof configData !== 'object') {
        console.error('❌ [useConfig] Invalid unified config data:', configData);
        throw new Error('Invalid unified configuration structure');
      }
      
      // Apply comprehensive normalization to ensure complete data structure
      const unifiedConfig = normalizeConfig(configData);
      
      setConfig(unifiedConfig);
      setLastUpdated(unifiedConfig.lastUpdated);
      
      console.log('✅ [useConfig] Config loaded successfully');
      
    } catch (fetchError) {
      console.error('❌ [useConfig] Error fetching config:', fetchError);
      setError(String(fetchError?.message || 'Failed to fetch configuration'));
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const updateConfig = useCallback(async (newConfig) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 [useConfig] Updating unified config:', newConfig);
      
      // Sanitize and validate config before sending
      const sanitizedConfig = sanitizeConfigForAPI(newConfig);
      console.log('🧹 [useConfig] Sanitized config:', sanitizedConfig);
      
      // Update configuration via API
      const updatedConfig = await updateConfigApi(siteId, sanitizedConfig);
      
      console.log('✅ [useConfig] Config updated successfully:', updatedConfig);
      
      // CRITICAL FIX: Force complete state refresh to ensure UI reflects saved changes
      setConfig(updatedConfig);
      setLastUpdated(updatedConfig.lastUpdated);
      
      // Force a complete re-fetch to ensure we have the latest server state
      setTimeout(() => {
        fetchConfig(siteId);
      }, 100);
      
    } catch (updateError) {
      console.error('❌ [useConfig] Error updating config:', updateError);
      setError(String(updateError?.message || 'Failed to update configuration'));
      throw updateError;
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const updatePlacement = useCallback(async (placementName, placementData) => {
    try {
      if (!config) {
        throw new Error('No configuration loaded');
      }
      
      console.log('🔄 [useConfig] Updating placement:', placementName, placementData);
      
      const updatedConfig = {
        ...config,
        placements: {
          ...config.placements,
          [placementName]: placementData
        },
        lastUpdated: new Date().toISOString()
      };
      
      await updateConfig(updatedConfig);
      
    } catch (error) {
      console.error('❌ [useConfig] Error updating placement:', error);
      throw error;
    }
  }, [config, updateConfig]);

  const deletePlacement = useCallback(async (placementName) => {
    try {
      if (!config) {
        throw new Error('No configuration loaded');
      }
      
      console.log('🔄 [useConfig] Deleting placement:', placementName);
      
      const updatedPlacements = { ...config.placements };
      delete updatedPlacements[placementName];
      
      const updatedConfig = {
        ...config,
        placements: updatedPlacements,
        lastUpdated: new Date().toISOString()
      };
      
      await updateConfig(updatedConfig);
      
    } catch (error) {
      console.error('❌ [useConfig] Error deleting placement:', error);
      throw error;
    }
  }, [config, updateConfig]);

  const addPlacement = useCallback(async (placementName, placementData) => {
    try {
      if (!config) {
        throw new Error('No configuration loaded');
      }
      
      console.log('🔄 [useConfig] Adding placement:', placementName, placementData);
      
      if (config.placements[placementName]) {
        throw new Error(`Placement '${placementName}' already exists`);
      }
      
      const updatedConfig = {
        ...config,
        placements: {
          ...config.placements,
          [placementName]: placementData
        },
        lastUpdated: new Date().toISOString()
      };
      
      await updateConfig(updatedConfig);
      
    } catch (error) {
      console.error('❌ [useConfig] Error adding placement:', error);
      throw error;
    }
  }, [config, updateConfig]);

  const deployConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 [useConfig] Deploying configuration for site:', siteId);
      
      const result = await deployApi(siteId);
      
      console.log('✅ [useConfig] Configuration deployed successfully:', result);
      
      return result;
      
    } catch (deployError) {
      console.error('❌ [useConfig] Error deploying configuration:', deployError);
      setError(String(deployError?.message || 'Failed to deploy configuration'));
      throw deployError;
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const refetch = useCallback(() => {
    fetchConfig(siteId);
  }, [fetchConfig, siteId]);

  // Clear state and fetch config when siteId changes
  useEffect(() => {
    console.log('🔄 [useConfig] Site changed from', siteId, 'to', initialSiteId);
    
    // CRITICAL FIX: Clear all state immediately when site changes to prevent cross-contamination
    setConfig(null);
    setError(null);
    setLastUpdated(null);
    setLoading(true);
    
    // Update siteId and fetch new configuration
    setSiteId(initialSiteId);
    fetchConfig(initialSiteId);
  }, [initialSiteId, fetchConfig]);

  return {
    config,
    loading,
    error,
    lastUpdated,
    updateConfig,
    updatePlacement,
    deletePlacement,
    addPlacement,
    deployConfig,
    refetch
  };
};